export const ALLOWED_LIMITS = [10, 30, 50, 100] as const;

export function isValidDateFormat(dateStr: string): boolean {
  if (!dateStr) {
    return false;
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return false;
  }
  return !isNaN(new Date(dateStr).getTime());
}

export function isValidDateRange(
  startDate?: string,
  endDate?: string
): boolean {
  if (!startDate || !endDate) {
    return true;
  }
  if (!isValidDateFormat(startDate) || !isValidDateFormat(endDate)) {
    return false;
  }
  return startDate <= endDate;
}

export function isValidStatus(status: string): boolean {
  return ['active', 'inactive'].includes(status);
}

export function validateStatusString(statusStr: string): boolean {
  if (!statusStr) {
    return true;
  }
  return statusStr.split(',').every((s) => isValidStatus(s.trim()));
}

export function sanitizeDateString(
  dateStr: string | null | undefined
): string | undefined {
  if (!dateStr) {
    return undefined;
  }
  const trimmed = String(dateStr).trim();
  return isValidDateFormat(trimmed) ? trimmed : undefined;
}

export function sanitizeStatusString(
  statusStr: string | null | undefined
): string | undefined {
  if (!statusStr) {
    return undefined;
  }
  const trimmed = String(statusStr).trim();
  if (!trimmed) {
    return undefined;
  }
  const valid = trimmed
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s && isValidStatus(s));
  return valid.length > 0 ? valid.join(',') : undefined;
}

export function processUrlParams(raw: RawUrlParams): ProcessedUrlParams {
  const startDate = sanitizeDateString(raw.startDate);
  const endDate = sanitizeDateString(raw.endDate);
  const status = sanitizeStatusString(raw.status);

  const pageNum = parseInt(raw.page ?? '1', 10);
  const page = isNaN(pageNum) || pageNum < 1 ? 1 : pageNum;

  const limitNum = parseInt(raw.limit ?? '10', 10);
  const limit = (ALLOWED_LIMITS as readonly number[]).includes(limitNum)
    ? limitNum
    : 10;

  const searchRaw = (raw.search ?? '').trim();
  const search =
    searchRaw.length > 200 ? searchRaw.substring(0, 200) : searchRaw;

  return {
    page,
    limit,
    search,
    startDate,
    endDate,
    status,
    isDateRangeValid: isValidDateRange(startDate, endDate),
    isStatusValid: validateStatusString(status ?? ''),
  };
}

// Pre-formats a date string once (after fetch) so render functions do no computation
const dateFormatter = new Intl.DateTimeFormat('pl-PL', {
  timeZone: 'Europe/Warsaw',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

export function formatAdminCreatedAt(dateStr: string): string {
  const parts = dateFormatter.formatToParts(new Date(dateStr));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}/${get('month')}/${get('day')}, ${get('hour')}:${get('minute')}:${get('second')}`;
}

export function getAdminInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 1);
}

export type RawUrlParams = {
  page: string | null;
  search: string | null;
  limit: string | null;
  startDate: string | null;
  endDate: string | null;
  status: string | null;
};

export type ProcessedUrlParams = {
  page: number;
  limit: number;
  search: string;
  startDate: string | undefined;
  endDate: string | undefined;
  status: string | undefined;
  isDateRangeValid: boolean;
  isStatusValid: boolean;
};

export type FormattedAdmin = {
  id: string;
  user_id: string;
  name: string;
  status: 'active' | 'inactive';
  created_at: string;
  formattedCreatedAt: string;
  initials: string;
};
